import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSaveForm } from '@/shared/lib/useSaveForm';
import { FormField } from '@/shared/ui/FormField';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { fetchSeoPage, saveSeoPage, type SeoPage, type SeoPageFormValues } from './api';

// Legacy edited both SEO texts in CKEditor. Here they are plain HTML textareas (no CKEditor yet),
// so the markup is edited as source and saved unchanged.

type Field = keyof SeoPageFormValues;

const INPUT_FIELDS: Array<{ field: Field; label: string; serverField: string }> = [
  { field: 'title', label: 'Title', serverField: 'title' },
  { field: 'h1', label: 'H1', serverField: 'short_description' },
  { field: 'description', label: 'Description', serverField: 'description' },
];

const TEXT_FIELDS: Array<{ field: Field; label: string; serverField: string }> = [
  { field: 'sellingSeoText', label: 'Продающий SEO текст', serverField: 'selling_seo_text' },
  { field: 'seoText', label: 'SEO текст', serverField: 'seo_text' },
];

const toFormValues = (page: SeoPage): SeoPageFormValues => ({
  title: page.title ?? '',
  h1: page.short_description ?? '',
  description: page.description ?? '',
  sellingSeoText: page.selling_seo_text ?? '',
  seoText: page.seo_text ?? '',
});

const SeoPageForm = ({ page }: { page: SeoPage }) => {
  const qc = useQueryClient();
  const [values, setValues] = useState(() => toFormValues(page));
  const [saved, setSaved] = useState(false);

  const { errors, clearErrors, save, isPending } = useSaveForm(
    (form: SeoPageFormValues) => saveSeoPage(page.id, form),
    () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['seo-pages'] });
    },
  );

  const patch = (field: Field, serverField: string, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
    clearErrors(serverField);
  };

  return (
    <>
      <h4>
        <strong>{`СТРАНИЦА:  ${page.url ?? ''}`}</strong>
      </h4>
      {INPUT_FIELDS.map(({ field, label, serverField }) => (
        <FormField key={field} label={label} errors={errors[serverField]}>
          <input
            className="form-control"
            placeholder={label.toLowerCase()}
            value={values[field]}
            onChange={(e) => patch(field, serverField, e.target.value)}
          />
        </FormField>
      ))}
      {TEXT_FIELDS.map(({ field, label, serverField }) => (
        <FormField key={field} label={label} errors={errors[serverField]}>
          <textarea
            className="form-control"
            rows={10}
            style={{ fontFamily: 'monospace' }}
            value={values[field]}
            onChange={(e) => patch(field, serverField, e.target.value)}
          />
        </FormField>
      ))}
      {(errors.common ?? errors.server)?.map((message) => (
        <p key={message} className="text-red">
          {message}
        </p>
      ))}
      <button type="button" className="btn btn-info pull-right" disabled={isPending} onClick={() => save(values)}>
        Сохранить
      </button>
      {saved && <span className="text-green">Данные сохранены успешно</span>}
    </>
  );
};

export const SeoPageEditPage = () => {
  const id = Number(useParams().id);
  const { data, isLoading, error } = useQuery({
    queryKey: ['seo-page', id],
    queryFn: () => fetchSeoPage(id),
  });

  return (
    <>
      <section className="content-header">
        <h1>Редактирование страницы</h1>
      </section>
      <section className="content">
        <div className="box box-primary">
          <div className="box-body">
            <QueryStatus isLoading={isLoading} error={error} errorText="Ошибка загрузки страницы" />
            {data && <SeoPageForm key={id} page={data} />}
          </div>
        </div>
      </section>
    </>
  );
};
